import { useState, useRef, useEffect } from "react";
import { ArrowRight, Send, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "./chatwidget.css";
import CalrizMascote from '../../assets/mascote.jpg'
import BrasaoUniversidade from '../../assets/brasaoUniversidadeCalriz-sem-fundo.png'

const UNIVERSIDADE_PATH = "/universidade-calriz";
// A Universidade fica no calriz.org: link com domínio (e não <Link>) para sair do calriz.com de verdade.
const UNIVERSIDADE_URL = `https://calriz.org${UNIVERSIDADE_PATH}`;

type Message = {
  role: "user" | "ai";
  content: string;
};

export default function ChatWidget() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  async function sendMessage() {
    const text = input.trim();
    if (!text || isSending) return;
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setIsSending(true);
    try {
      const res = await fetch("https://backend.calriz.com/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "ai", content: data.reply }]);
    } catch (error) {
      console.error("Erro no chat:", error);
      setMessages((prev) => [...prev, { role: "ai", content: t("chat.error", "Não consegui responder agora. Tente novamente em instantes.") }]);
    } finally {
      setIsSending(false);
    }
  }

  // A saudação é derivada do idioma atual em vez de ficar no estado.
  const allMessages: Message[] = [
    { role: "ai", content: t("chat.greeting", "Olá, eu sou a Rizza, a assistente virtual da Calriz. Como posso ajudar?") },
    ...messages,
  ];

  return (
    <div className="cz-chat cz-scope">
      {/* Atalho para a Universidade Calriz, acima da Rizza; some com o chat aberto e na própria página. */}
      {!isOpen && pathname !== UNIVERSIDADE_PATH && (
        <a href={UNIVERSIDADE_URL} className="cz-chat__uni">
          <img src={BrasaoUniversidade} alt="" className="cz-chat__uni-brasao" />
          <strong className="cz-chat__uni-title">{t("university_fab.title", "Universidade Calriz")}</strong>
          <span className="cz-chat__uni-cta">
            {t("university_fab.cta", "Conhecer")}
            <ArrowRight size={14} aria-hidden="true" />
          </span>
        </a>
      )}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="cz-chat__launcher"
          aria-label={t("chat.open", "Abrir assistente virtual")}
        >
          <img src={CalrizMascote} alt="" />
        </button>
      )}
      {isOpen && (
        <div className="cz-chat__window" role="dialog" aria-label={t("chat.title", "Assistente Rizza")}>
          <div className="cz-chat__header">
            <img src={CalrizMascote} alt="" className="cz-chat__header-avatar" />
            <span>{t("chat.title", "Assistente Rizza")}</span>
            <button type="button" onClick={() => setIsOpen(false)} aria-label={t("chat.close", "Fechar assistente")}>
              <X size={18} />
            </button>
          </div>
          <div className="cz-chat__messages" aria-live="polite">
            {allMessages.map((m, i) => (
              <div key={i} className={`cz-chat__msg cz-chat__msg--${m.role}`}>
                {m.role === "ai" && <img src={CalrizMascote} alt="" className="cz-chat__avatar" />}
                <span className="cz-chat__bubble">{m.content}</span>
              </div>
            ))}
            {isSending && (
              <div className="cz-chat__msg cz-chat__msg--ai">
                <img src={CalrizMascote} alt="" className="cz-chat__avatar" />
                <span className="cz-chat__bubble cz-chat__typing" aria-label="...">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="cz-chat__input">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder", "Digite sua dúvida...")}
              aria-label={t("chat.placeholder", "Digite sua dúvida...")}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button type="button" onClick={sendMessage} disabled={isSending || !input.trim()} aria-label={t("chat.send", "Enviar")}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
